import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllParties } from "../slices/partySlice";
import { getAllProducts } from "../slices/productSlice";
import { createBill, getAllBills, getBillById, updateBill, resetCreate } from "../slices/billSlice";
import { toast } from "react-toastify";
import { getLineTotal, getRateFromAmount, applyGrandTotalToItems, sumBillLineTotals } from "../utils/billLineCalc";
import billService from "../services/billService";

const BillCreationModal = ({ open, onClose, preselectedPartyId, editBillId }) => {
  const dispatch = useDispatch();
  const { parties = [] } = useSelector((state) => state.party);
  const { products = [] } = useSelector((state) => state.product);
  const { bills = [], currentBill, createLoading, createSuccess, updateLoading } = useSelector((state) => state.bill);
  const isEditMode = Boolean(editBillId);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [formData, setFormData] = useState({
    partyId: "",
    billDate: new Date().toISOString().split("T")[0],
    billNumber: "",
    billingName: "",
    billType: "withoutTax",
    notes: "",
    status: "pending",
  });
  const [customerSearch, setCustomerSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [amountDraft, setAmountDraft] = useState(null);
  const [lineAmountDrafts, setLineAmountDrafts] = useState({});
  const [saveAndNewRequested, setSaveAndNewRequested] = useState(false);
  const [billItems, setBillItems] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [selectedRate, setSelectedRate] = useState(0);
  const [selectedGstRate, setSelectedGstRate] = useState(18);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const loadNextInvoiceNumber = async () => {
    try {
      const res = await billService.getNextInvoiceNumber();
      const next = res?.nextInvoiceNumber || "";
      if (next) setFormData((p) => ({ ...p, billNumber: next }));
    } catch {
      /* keep current value */
    }
  };

  useEffect(() => {
    if (open) {
      dispatch(getAllParties({ page: 1, limit: 100 }));
      dispatch(getAllProducts({ page: 1, limit: 100 }));
      dispatch(getAllBills());
      if (editBillId) dispatch(getBillById(editBillId));
      else loadNextInvoiceNumber();
    }
  }, [open, dispatch, editBillId]);

  useEffect(() => {
    if (open && preselectedPartyId && !editBillId && (parties || []).length > 0) {
      const party = (parties || []).find((p) => p._id === preselectedPartyId);
      if (party) {
        const name = party.partyName || party.companyName || "";
        setFormData((p) => ({ ...p, partyId: preselectedPartyId, billingName: name }));
        setCustomerSearch(name);
      }
    }
  }, [open, preselectedPartyId, parties, editBillId]);

  // When opening for edit, clear form until the right bill loads (avoid showing previous bill)
  useEffect(() => {
    if (open && editBillId && (!currentBill || currentBill._id !== editBillId)) {
      setBillItems([]);
      setFormData({ partyId: "", billDate: new Date().toISOString().split("T")[0], billNumber: "", billingName: "", billType: "withoutTax", notes: "", status: "pending" });
      setCustomerSearch("");
    }
  }, [open, editBillId, currentBill]);

  // Populate form and items when editing and currentBill is loaded
  useEffect(() => {
    if (!open || !editBillId || !currentBill || currentBill._id !== editBillId) return;
    const full = currentBill;
    const partyId = full.partyId?._id || full.partyId;
    const partyName = full.party?.partyName || full.party?.companyName || "";
    const bt = full.billType;
    const isKachu = bt === "kachuBill" || bt === "kachuBillGstIncl" || bt === "delivery";
    const billType = isKachu ? "kachuBill" : (bt === "withTaxIgst" || bt === "withoutTaxIgst" ? "withoutTaxIgst" : "withoutTax");
    const gstRate = (full.items?.[0]?.gstRate === 18 || full.items?.[0]?.gstRate === 5) ? full.items[0].gstRate : 18;
    setFormData({
      partyId,
      billDate: full.billDate ? new Date(full.billDate).toISOString().split("T")[0] : new Date().toISOString().split("T")[0],
      billNumber: full.billNumber || "",
      billingName: full.billingName || partyName,
      billType,
      notes: full.notes || "",
      status: full.status || "pending",
    });
    setCustomerSearch(partyName);
    const items = (full.items || []).map((item) => ({
      productId: item.productId?._id || item.productId,
      productName: item.productName,
      hsnCode: item.hsnCode || "",
      unit: item.unit || "",
      quantity: item.quantity,
      rate: item.rate,
      gstRate: item.gstRate ?? (isKachu ? 0 : gstRate),
    }));
    setBillItems(items);
    setSelectedGstRate(isKachu ? 0 : gstRate);
  }, [open, editBillId, currentBill]);

  const handlePartyChange = (partyId) => {
    const party = (parties || []).find((p) => p._id === partyId);
    setFormData((p) => ({
      ...p,
      partyId,
      billingName: party ? (party.partyName || party.companyName || "") : p.billingName,
    }));
    setCustomerSearch(party ? (party.partyName || party.companyName || "") : "");
    setShowPartyDropdown(false);
  };

  const filteredParties = useMemo(() => {
    const active = (parties || []).filter((p) => p.isActive !== false);
    if (!customerSearch.trim()) return active.slice(0, 20);
    const q = customerSearch.toLowerCase().trim();
    return active.filter((p) => (p.partyName || "").toLowerCase().includes(q) || (p.companyName || "").toLowerCase().includes(q) || (p.mobileNumber || "").includes(q)).slice(0, 20);
  }, [parties, customerSearch]);

  const filteredProducts = useMemo(() => {
    const active = (products || []).filter((p) => p.isActive !== false);
    if (!productSearch.trim()) return active.slice(0, 20);
    const q = productSearch.toLowerCase().trim();
    return active.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.hsnCode || "").toLowerCase().includes(q)).slice(0, 20);
  }, [products, productSearch]);

  const selectedParty = useMemo(() => (parties || []).find((p) => p._id === formData.partyId), [parties, formData.partyId]);

  const selectedLineAmount = useMemo(
    () =>
      getLineTotal({
        rate: selectedRate,
        quantity: selectedQuantity,
        gstRate: selectedGstRate,
        billType: formData.billType,
      }),
    [selectedRate, selectedQuantity, selectedGstRate, formData.billType]
  );

  const applySelectedAmount = (value) => {
    const amount = value === "" ? 0 : parseFloat(value) || 0;
    if (selectedQuantity > 0 && amount > 0) {
      setSelectedRate(
        getRateFromAmount({
          amount,
          quantity: selectedQuantity,
          gstRate: selectedGstRate,
          billType: formData.billType,
        })
      );
    } else if (value === "" || amount <= 0) {
      setSelectedRate(0);
    }
  };

  const selectDropdownItem = (handler) => (e) => {
    e.preventDefault();
    handler();
  };

  // With Tax: rate = price inclusive of GST. Without Tax: rate = base, GST added on top
  const totals = useMemo(() => {
    let subtotal = 0, totalGst = 0;
    const billType = formData.billType || "withoutTax";
    const isKachha = billType === "kachuBill" || billType === "kachuBillGstIncl" || billType === "delivery";
    billItems.forEach((item) => {
      const rate = item.rate || 0, qty = item.quantity || 0, gstRate = isKachha ? 0 : (item.gstRate || 0);
      let itemBase, itemGst;
      if (isKachha) {
        itemBase = rate * qty;
        itemGst = 0;
      } else if (billType === "withTax" || billType === "withTaxIgst") {
        const itemTotal = rate * qty;
        itemBase = gstRate > 0 ? itemTotal / (1 + gstRate / 100) : itemTotal;
        itemGst = itemTotal - itemBase;
      } else {
        itemBase = rate * qty;
        itemGst = (itemBase * gstRate) / 100;
      }
      subtotal += itemBase;
      totalGst += itemGst;
    });
    const totalAmount = subtotal + totalGst;
    const lineGrandTotal = sumBillLineTotals(billItems, billType);
    const grandTotal = lineGrandTotal > 0 ? lineGrandTotal : parseFloat(totalAmount.toFixed(2));
    return { subtotal: parseFloat(subtotal.toFixed(2)), totalGst: parseFloat(totalGst.toFixed(2)), totalAmount: parseFloat(totalAmount.toFixed(2)), roundOff: 0, grandTotal };
  }, [billItems, formData.billType]);

  const handleProductSelect = (productId) => {
    const product = (products || []).find((p) => p._id === productId);
    if (product) {
      setSelectedProductId(productId);
      setSelectedRate(product.price);
      setSelectedQuantity(1);
      if (formData.billType === "kachuBill" || formData.billType === "kachuBillGstIncl") setSelectedGstRate(0);
      else if (formData.billType !== "withoutTax" && formData.billType !== "withoutTaxIgst") setSelectedGstRate(product.gstRate ?? 18);
      setProductSearch(product.name || "");
      setShowProductDropdown(false);
      setAmountDraft(null);
    }
  };

  const handleAddProduct = () => {
    const errors = [];
    if (!selectedProductId) errors.push("Product");
    const product = (products || []).find((p) => p._id === selectedProductId);
    if (selectedProductId && !product) errors.push("Product (not found)");
    if (selectedQuantity <= 0 || selectedQuantity === undefined || selectedQuantity === null) errors.push("Qty (must be > 0)");
    const rate = Number(selectedRate);
    if (rate === 0 || rate === undefined || isNaN(rate)) errors.push("Rate");

    if (errors.length > 0) {
      toast.error("Please fill: " + errors.join(", "));
      return;
    }
    const idx = billItems.findIndex((i) => i.productId === selectedProductId);
    if (idx >= 0) {
      const u = [...billItems];
      u[idx] = { ...u[idx], quantity: selectedQuantity, rate: selectedRate, gstRate: selectedGstRate };
      setBillItems(u);
    } else {
      setBillItems([...billItems, { productId: selectedProductId, productName: product.name, hsnCode: product.hsnCode, unit: product.unit, quantity: selectedQuantity, rate: selectedRate, gstRate: selectedGstRate }]);
    }
    setSelectedProductId("");
    setSelectedQuantity(1);
    setSelectedRate(0);
    setProductSearch("");
    setShowProductDropdown(false);
    setAmountDraft(null);
    if (formData.billType === "kachuBill" || formData.billType === "kachuBillGstIncl") setSelectedGstRate(0);
    else if (formData.billType !== "withoutTax" && formData.billType !== "withoutTaxIgst") setSelectedGstRate(18);
  };

  const handleRemoveItem = (index) => setBillItems(billItems.filter((_, i) => i !== index));

  const handleGrandTotalChange = (value) => {
    const target = value === "" ? 0 : parseFloat(value) || 0;
    if (billItems.length === 0) return;
    if (target <= 0) {
      setBillItems((prev) => prev.map((it) => ({ ...it, rate: 0 })));
      return;
    }
    setBillItems(applyGrandTotalToItems(billItems, target, formData.billType));
  };

  // Inline edit: update qty, rate, gstRate, or line amount (amount → auto price).
  const handleUpdateItem = (index, field, value) => {
    const isEmpty = value === "";
    const num = parseFloat(value);
    setBillItems((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      const item = next[index];
      const billType = formData.billType || "withoutTax";
      if (field === "quantity") {
        next[index] = { ...item, quantity: isEmpty ? 0 : (!isNaN(num) && num >= 0 ? num : item.quantity) };
      } else if (field === "rate") {
        next[index] = { ...item, rate: isEmpty ? 0 : (!isNaN(num) && num >= 0 ? num : item.rate) };
      } else if (field === "gstRate") {
        next[index] = { ...item, gstRate: isEmpty ? 0 : (!isNaN(num) && num >= 0 ? num : item.gstRate) };
      } else if (field === "lineAmount") {
        const qty = item.quantity || 0;
        const amount = isEmpty ? 0 : !isNaN(num) && num >= 0 ? num : 0;
        if (qty > 0 && amount >= 0) {
          const isKachha =
            billType === "kachuBill" || billType === "kachuBillGstIncl" || billType === "delivery";
          const rate = getRateFromAmount({
            amount,
            quantity: qty,
            gstRate: isKachha ? 0 : item.gstRate || 0,
            billType,
          });
          next[index] = { ...item, rate };
        }
      }
      return next;
    });
  };

  const handleCreateBill = async (saveAndNew = false) => {
    if (!formData.partyId) { toast.error("Please select a party"); return; }
    if (billItems.length === 0) { toast.error("Please add at least one product"); return; }
    setSaveAndNewRequested(saveAndNew);
    const billData = {
      partyId: formData.partyId,
      items: billItems.map((i) => ({ productId: i.productId, quantity: i.quantity, rate: i.rate, gstRate: (formData.billType === "kachuBill" || formData.billType === "kachuBillGstIncl") ? 0 : (i.gstRate ?? 18) })),
      billDate: formData.billDate,
      status: formData.status,
      notes: formData.notes,
      billNumber: formData.billNumber?.trim() || undefined,
      billType: formData.billType || undefined,
      billingName: formData.billingName?.trim() || undefined,
    };
    try {
      if (isEditMode && editBillId) {
        await dispatch(updateBill({ billId: editBillId, billData })).unwrap();
        onClose();
      } else {
        await dispatch(createBill(billData)).unwrap();
        if (saveAndNew) {
          const keepInvoice = formData.billNumber?.trim() || "";
          setFormData({
            partyId: "",
            billDate: new Date().toISOString().split("T")[0],
            billNumber: keepInvoice,
            billingName: "",
            billType: formData.billType || "withoutTax",
            notes: "",
            status: "pending",
          });
          setCustomerSearch("");
          setBillItems([]);
          setSelectedProductId("");
          setSelectedQuantity(1);
          setSelectedRate(0);
          setSelectedGstRate(18);
          setProductSearch("");
          dispatch(getAllBills());
        } else {
          onClose();
        }
      }
    } catch (e) {
      setSaveAndNewRequested(false);
    }
  };

  useEffect(() => {
    if (createSuccess) {
      setSaveAndNewRequested(false);
      dispatch(resetCreate());
    }
  }, [createSuccess, dispatch]);


  const styles = {
    modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: isMobile ? "flex-start" : "center", justifyContent: "center", zIndex: 1002, padding: isMobile ? 16 : 24, overflowY: "auto" },
    modalContent: { backgroundColor: "#fff", borderRadius: isMobile ? 0 : 8, padding: isMobile ? 20 : 32, maxWidth: 800, width: "100%", maxHeight: isMobile ? "100vh" : "90vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.24)", display: "flex", flexDirection: "column" },
    modalCloseButton: { background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#667781", padding: 0, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "50%" },
    label: { display: "block", fontSize: 14, fontWeight: 500, color: "#111b21", marginBottom: 8 },
    input: { width: "100%", padding: isMobile ? "12px 14px" : "10px 12px", fontSize: isMobile ? 16 : 15, border: "1px solid #e9edef", borderRadius: 6, backgroundColor: "#fff", outline: "none", boxSizing: "border-box" },
    select: { width: "100%", padding: isMobile ? "12px 14px" : "10px 12px", fontSize: isMobile ? 16 : 15, border: "1px solid #e9edef", borderRadius: 6, backgroundColor: "#fff", outline: "none", cursor: "pointer", boxSizing: "border-box" },
    itemsSection: { marginTop: 20, paddingTop: 20, borderTop: "1px solid #e9edef" },
    addItemButton: { padding: isMobile ? "14px 16px" : "10px 16px", backgroundColor: "#25d366", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: isMobile ? 15 : 13, fontWeight: 500, width: isMobile ? "100%" : undefined },
    fieldInput: { width: "100%", padding: isMobile ? "12px 10px" : "6px 8px", fontSize: isMobile ? 16 : 13, border: "1px solid #e9edef", borderRadius: 6, backgroundColor: "#fff", outline: "none", boxSizing: "border-box" },
    itemCard: { backgroundColor: "#f8f9fa", border: "1px solid #e9edef", borderRadius: 10, padding: isMobile ? 14 : 12, marginBottom: 12 },
    itemCardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 10 },
    itemCardTitle: { fontSize: isMobile ? 15 : 14, fontWeight: 600, color: "#111b21", lineHeight: 1.35, flex: 1 },
    itemCardMeta: { fontSize: 12, color: "#667781", marginBottom: 10 },
    itemFieldGrid: { display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: isMobile ? 10 : 8 },
    itemFieldLabel: { fontSize: 11, color: "#667781", display: "block", marginBottom: 4, fontWeight: 500 },
    itemsTable: { width: "100%", borderCollapse: "collapse", marginBottom: 20 },
    itemsTableHeader: { backgroundColor: "#f0f2f5", padding: 10, textAlign: "left", fontSize: 12, fontWeight: 600, color: "#667781", borderBottom: "2px solid #e9edef" },
    itemsTableCell: { padding: 10, fontSize: 13, color: "#111b21", borderBottom: "1px solid #f0f2f5" },
    removeItemButton: { background: "none", border: "none", color: "#f15c6d", cursor: "pointer", fontSize: 16, padding: "4px 8px" },
    totalsSection: { marginTop: 20, paddingTop: 20, borderTop: "2px solid #e9edef" },
    totalRow: { display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: 14, color: "#667781" },
    grandTotalRow: { display: "flex", justifyContent: "space-between", padding: "12px 0", fontSize: 18, fontWeight: 600, color: "#111b21", borderTop: "2px solid #e9edef", marginTop: 8 },
    textarea: { width: "100%", padding: "10px 12px", fontSize: 15, border: "1px solid #e9edef", borderRadius: 6, minHeight: 80, resize: "vertical" },
    modalActions: { display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 24, paddingTop: 20, borderTop: "1px solid #e9edef" },
    modalButton: { padding: "10px 20px", fontSize: 14, fontWeight: 500, border: "none", borderRadius: 6, cursor: "pointer" },
  };

  if (!open) return null;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 12, borderBottom: "1px solid #e9edef" }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 20, fontWeight: 600 }}>{isEditMode ? "Edit Bill" : "Sale"}</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: "1px solid #e9edef" }}>
              <button type="button" onClick={() => setFormData((p) => ({ ...p, status: "pending" }))} style={{ padding: "6px 14px", border: "none", background: formData.status === "pending" ? "#e9edef" : "#fff", color: formData.status === "pending" ? "#111b21" : "#667781", fontWeight: 500, fontSize: 13, cursor: "pointer" }}>Credit</button>
              <button type="button" onClick={() => setFormData((p) => ({ ...p, status: "paid" }))} style={{ padding: "6px 14px", border: "none", background: formData.status === "paid" ? "#25d366" : "#fff", color: formData.status === "paid" ? "#fff" : "#667781", fontWeight: 500, fontSize: 13, cursor: "pointer" }}>Cash</button>
            </div>
            <button onClick={onClose} style={styles.modalCloseButton}>×</button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 16 : 20 }}>
          <div style={{ display: "flex", flexDirection: "row", gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={{ ...styles.label, fontSize: 12 }}>Invoice No.</label>
              <input
                type="text"
                value={formData.billNumber}
                onChange={(e) => setFormData((p) => ({ ...p, billNumber: e.target.value }))}
                placeholder="Auto next number"
                style={styles.input}
                title="Same number for many bills (e.g. 10 for Person A, B, C). Save & New keeps this number."
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}><label style={{ ...styles.label, fontSize: 12 }}>Date</label><input type="date" value={formData.billDate} onChange={(e) => setFormData((p) => ({ ...p, billDate: e.target.value }))} style={styles.input} /></div>
          </div>
          <div style={{ marginBottom: 16, position: "relative" }}>
            <label style={{ ...styles.label, fontSize: 12 }}>Customer</label>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <input type="text" value={customerSearch} onChange={(e) => { setCustomerSearch(e.target.value); if (!e.target.value) setFormData((p) => ({ ...p, partyId: "", billingName: "" })); setShowPartyDropdown(true); }} onFocus={() => setShowPartyDropdown(true)} onBlur={() => setTimeout(() => setShowPartyDropdown(false), 200)} placeholder="Search customer..." style={{ ...styles.input, flex: 1, minWidth: 120 }} />
              {selectedParty && <span style={{ fontSize: 13, color: "#667781", fontWeight: 500 }}>Party Balance: ₹ {((selectedParty.totalDue ?? 0) - (selectedParty.totalPaid ?? 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>}
            </div>
            {showPartyDropdown && <div style={{ position: "absolute", top: "100%", left: 0, right: 0, maxHeight: 200, overflowY: "auto", background: "#fff", border: "1px solid #e9edef", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 10 }}>{(filteredParties || []).map((p) => <div key={p._id} onMouseDown={selectDropdownItem(() => handlePartyChange(p._id))} style={{ padding: "10px 12px", cursor: "pointer", borderBottom: "1px solid #f0f2f5", fontSize: 14 }}>{p.partyName || p.companyName} – {p.mobileNumber}</div>)}</div>}
          </div>
          <div style={styles.itemsSection}>
            <label style={styles.label}>Add Items</label>
            {/* Row 1: Product selection dropdown - full width */}
            <div style={{ position: "relative", marginBottom: isMobile ? 12 : 12, width: "100%" }}>
              <label style={{ fontSize: 11, color: "#667781", display: "block", marginBottom: 4 }}>Product</label>
              <input type="text" value={productSearch} onChange={(e) => { setProductSearch(e.target.value); if (!e.target.value) setSelectedProductId(""); setShowProductDropdown(true); }} onFocus={() => setShowProductDropdown(true)} onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)} placeholder="Search product..." style={styles.input} />
              {showProductDropdown && <div style={{ position: "absolute", top: "100%", left: 0, right: 0, maxHeight: 180, overflowY: "auto", background: "#fff", border: "1px solid #e9edef", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 10 }}>{(filteredProducts || []).map((p) => <div key={p._id} onMouseDown={selectDropdownItem(() => handleProductSelect(p._id))} style={{ padding: "10px 12px", cursor: "pointer", borderBottom: "1px solid #f0f2f5", fontSize: 14 }}>{p.name} – ₹{p.price}/{p.unit}</div>)}</div>}
            </div>
            {/* Row 2: Qty | Price | Amount — stacks on mobile for easy typing */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div style={{ minWidth: 0 }}>
                <label style={{ fontSize: 11, color: "#667781", display: "block", marginBottom: 4 }}>Qty</label>
                <input type="number" value={selectedQuantity === 0 ? "" : selectedQuantity} onChange={(e) => setSelectedQuantity(e.target.value === "" ? 0 : (parseFloat(e.target.value) || 0))} min="0" step="0.01" style={styles.input} placeholder="1" />
              </div>
              <div style={{ minWidth: 0 }}>
                <label style={{ fontSize: 11, color: "#667781", display: "block", marginBottom: 4 }}>Price (₹)</label>
                <input type="number" value={selectedRate === 0 ? "" : selectedRate} onChange={(e) => setSelectedRate(e.target.value === "" ? 0 : (parseFloat(e.target.value) || 0))} min="0" step="0.01" style={styles.input} placeholder="0" />
              </div>
              <div style={{ minWidth: 0, gridColumn: isMobile ? "1 / -1" : undefined }}>
                <label style={{ fontSize: 11, color: "#667781", display: "block", marginBottom: 4 }}>Amount (₹)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={amountDraft !== null ? amountDraft : (selectedLineAmount === 0 ? "" : selectedLineAmount.toFixed(2))}
                  onFocus={() => setAmountDraft(selectedLineAmount === 0 ? "" : selectedLineAmount.toFixed(2))}
                  onChange={(e) => setAmountDraft(e.target.value)}
                  onBlur={() => {
                    if (amountDraft !== null) {
                      applySelectedAmount(amountDraft);
                      setAmountDraft(null);
                    }
                  }}
                  style={styles.input}
                  placeholder="0"
                  title="Enter line total — price updates automatically (qty stays same)"
                />
              </div>
            </div>
            {/* Row 3: GST + ADD — full width on mobile */}
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 12, alignItems: isMobile ? "stretch" : "flex-end" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ fontSize: 11, color: "#667781", display: "block", marginBottom: 4 }}>GST</label>
                {(formData.billType === "withoutTax" || formData.billType === "withoutTaxIgst") ? (
                  <select
                    value={formData.billType === "withoutTaxIgst" ? (selectedGstRate === 18 ? "igst18" : "igst5") : (selectedGstRate === 18 ? "gst18" : "gst5")}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "gst5") { setFormData((p) => ({ ...p, billType: "withoutTax" })); setSelectedGstRate(5); }
                      else if (v === "gst18") { setFormData((p) => ({ ...p, billType: "withoutTax" })); setSelectedGstRate(18); }
                      else if (v === "igst5") { setFormData((p) => ({ ...p, billType: "withoutTaxIgst" })); setSelectedGstRate(5); }
                      else { setFormData((p) => ({ ...p, billType: "withoutTaxIgst" })); setSelectedGstRate(18); }
                    }}
                    style={{ ...styles.select, width: "100%" }}
                  >
                    <option value="gst5">GST 5%</option>
                    <option value="gst18">GST 18%</option>
                    <option value="igst5">IGST 5%</option>
                    <option value="igst18">IGST 18%</option>
                  </select>
                ) : (
                  <div style={{ padding: "10px 12px", fontSize: 13, color: "#667781", border: "1px solid #e9edef", borderRadius: 6, backgroundColor: "#f7f8fa" }}>— (Kachu Bill)</div>
                )}
              </div>
              <button type="button" onClick={handleAddProduct} style={{ ...styles.addItemButton, minWidth: isMobile ? undefined : 90, flexShrink: 0 }}>+ ADD</button>
            </div>
            {billItems.length > 0 && (
              <div style={{ marginTop: 16 }}>
                {isMobile ? (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#667781", marginBottom: 10 }}>Items ({billItems.length})</div>
                    {billItems.map((item, index) => {
                      const billType = formData.billType || "kachuBill";
                      const isKachha = billType === "kachuBill" || billType === "kachuBillGstIncl" || billType === "delivery";
                      const r = item.rate || 0, q = item.quantity || 0, g = isKachha ? 0 : (item.gstRate || 0);
                      const lineTotal = getLineTotal({ rate: r, quantity: q, gstRate: g, billType });
                      return (
                        <div key={index} style={styles.itemCard}>
                          <div style={styles.itemCardHeader}>
                            <div style={styles.itemCardTitle}>{item.productName}</div>
                            <button type="button" onClick={() => handleRemoveItem(index)} style={{ ...styles.removeItemButton, fontSize: 20, padding: "2px 6px" }} aria-label="Remove item">✕</button>
                          </div>
                          <div style={styles.itemCardMeta}>HSN: {item.hsnCode || "—"} · {item.unit || "—"}</div>
                          <div style={styles.itemFieldGrid}>
                            <div>
                              <label style={styles.itemFieldLabel}>Qty</label>
                              <input type="number" min="0" step="0.01" value={q === 0 ? "" : q} onChange={(e) => handleUpdateItem(index, "quantity", e.target.value)} placeholder="0" style={styles.fieldInput} />
                            </div>
                            <div>
                              <label style={styles.itemFieldLabel}>Rate (₹)</label>
                              <input type="number" min="0" step="0.01" value={r === 0 ? "" : r} onChange={(e) => handleUpdateItem(index, "rate", e.target.value)} placeholder="0" style={styles.fieldInput} />
                            </div>
                            <div>
                              <label style={styles.itemFieldLabel}>GST %</label>
                              {isKachha ? (
                                <div style={{ ...styles.fieldInput, backgroundColor: "#f0f2f5", color: "#667781", display: "flex", alignItems: "center" }}>—</div>
                              ) : (
                                <input type="number" min="0" max="100" step="0.01" value={g === 0 ? "" : g} onChange={(e) => handleUpdateItem(index, "gstRate", e.target.value)} placeholder="0" style={styles.fieldInput} />
                              )}
                            </div>
                            <div>
                              <label style={styles.itemFieldLabel}>Total (₹)</label>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={lineAmountDrafts[index] !== undefined ? lineAmountDrafts[index] : (lineTotal === 0 ? "" : lineTotal.toFixed(2))}
                                onFocus={() => setLineAmountDrafts((prev) => ({ ...prev, [index]: lineTotal === 0 ? "" : lineTotal.toFixed(2) }))}
                                onChange={(e) => setLineAmountDrafts((prev) => ({ ...prev, [index]: e.target.value }))}
                                onBlur={(e) => {
                                  handleUpdateItem(index, "lineAmount", e.target.value);
                                  setLineAmountDrafts((prev) => {
                                    const next = { ...prev };
                                    delete next[index];
                                    return next;
                                  });
                                }}
                                placeholder="0"
                                style={styles.fieldInput}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                <table style={styles.itemsTable}><thead><tr><th style={styles.itemsTableHeader}>Product</th><th style={styles.itemsTableHeader}>Qty</th><th style={styles.itemsTableHeader}>Rate</th><th style={styles.itemsTableHeader}>GST%</th><th style={styles.itemsTableHeader}>Total</th><th /></tr></thead><tbody>
                  {billItems.map((item, index) => {
                    const billType = formData.billType || "kachuBill";
                    const isKachha = billType === "kachuBill" || billType === "kachuBillGstIncl" || billType === "delivery";
                    const r = item.rate || 0, q = item.quantity || 0, g = isKachha ? 0 : (item.gstRate || 0);
                    const lineTotal = getLineTotal({ rate: r, quantity: q, gstRate: g, billType });
                    return (
                      <tr key={index}>
                        <td style={styles.itemsTableCell}>{item.productName} ({item.hsnCode}) {item.unit}</td>
                        <td style={styles.itemsTableCell}>
                          <input type="number" min="0" step="0.01" value={q === 0 ? "" : q} onChange={(e) => handleUpdateItem(index, "quantity", e.target.value)} placeholder="0" style={{ width: "72px", padding: "6px 8px", fontSize: 13, border: "1px solid #e9edef", borderRadius: 4 }} />
                        </td>
                        <td style={styles.itemsTableCell}>
                          <input type="number" min="0" step="0.01" value={r === 0 ? "" : r} onChange={(e) => handleUpdateItem(index, "rate", e.target.value)} placeholder="0" style={{ width: "80px", padding: "6px 8px", fontSize: 13, border: "1px solid #e9edef", borderRadius: 4 }} />
                        </td>
                        <td style={styles.itemsTableCell}>
                          {isKachha ? "—" : (
                            <input type="number" min="0" max="100" step="0.01" value={g === 0 ? "" : g} onChange={(e) => handleUpdateItem(index, "gstRate", e.target.value)} placeholder="0" style={{ width: "56px", padding: "6px 8px", fontSize: 13, border: "1px solid #e9edef", borderRadius: 4 }} />
                          )}
                        </td>
                        <td style={styles.itemsTableCell}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={lineAmountDrafts[index] !== undefined ? lineAmountDrafts[index] : (lineTotal === 0 ? "" : lineTotal.toFixed(2))}
                            onFocus={() => setLineAmountDrafts((prev) => ({ ...prev, [index]: lineTotal === 0 ? "" : lineTotal.toFixed(2) }))}
                            onChange={(e) => setLineAmountDrafts((prev) => ({ ...prev, [index]: e.target.value }))}
                            onBlur={(e) => {
                              handleUpdateItem(index, "lineAmount", e.target.value);
                              setLineAmountDrafts((prev) => {
                                const next = { ...prev };
                                delete next[index];
                                return next;
                              });
                            }}
                            placeholder="0"
                            title="Edit amount — price updates when you leave this field"
                            style={{ width: "80px", padding: "6px 8px", fontSize: 13, border: "1px solid #e9edef", borderRadius: 4 }}
                          />
                        </td>
                        <td style={styles.itemsTableCell}><button type="button" onClick={() => handleRemoveItem(index)} style={styles.removeItemButton}>✕</button></td>
                      </tr>
                    );
                  })}
                </tbody></table>
                )}
                <div style={styles.totalsSection}>
                  <div style={styles.totalRow}><span>Subtotal:</span><span>₹{totals.subtotal.toFixed(2)}</span></div>
                  {totals.totalGst > 0 && <div style={styles.totalRow}><span>Total GST:</span><span>₹{totals.totalGst.toFixed(2)}</span></div>}
                  {totals.roundOff !== 0 && <div style={styles.totalRow}><span>Round Off:</span><span>₹{totals.roundOff.toFixed(2)}</span></div>}
                  <div style={styles.grandTotalRow}>
                    <span>Total Amount:</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={totals.grandTotal === 0 ? "" : totals.grandTotal}
                      onChange={(e) => handleGrandTotalChange(e.target.value)}
                      title="Enter bill total — prices adjust automatically (qty stays same)"
                      style={{ width: 120, padding: "8px 10px", fontSize: 18, fontWeight: 600, textAlign: "right", border: "2px solid #25d366", borderRadius: 6, outline: "none" }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          
        </div>
        <div style={{ ...styles.modalActions, paddingTop: 16, borderTop: "1px solid #e9edef", gap: 12, flexWrap: isMobile ? "wrap" : "nowrap", justifyContent: isMobile ? "stretch" : "flex-end" }}>
          <button onClick={onClose} style={{ ...styles.modalButton, backgroundColor: "#f0f2f5", color: "#111b21", flex: isMobile ? "1 1 100%" : undefined }}>Cancel</button>
          {!isEditMode && (
            <button onClick={() => handleCreateBill(true)} disabled={createLoading || !formData.partyId || billItems.length === 0} style={{ ...styles.modalButton, background: "#fff", color: "#111b21", border: "1px solid #e9edef", flex: isMobile ? "1 1 calc(50% - 6px)" : undefined }}>{createLoading && saveAndNewRequested ? "..." : "Save & New"}</button>
          )}
          <button onClick={() => handleCreateBill(false)} disabled={createLoading || updateLoading || !formData.partyId || billItems.length === 0} style={{ ...styles.modalButton, backgroundColor: "#25d366", color: "#fff", opacity: createLoading || updateLoading || !formData.partyId || billItems.length === 0 ? 0.5 : 1, flex: isMobile ? "1 1 calc(50% - 6px)" : undefined }}>{updateLoading ? "Updating..." : createLoading && !saveAndNewRequested ? "Saving..." : isEditMode ? "Update" : "Save"}</button>
        </div>
      </div>
    </div>
  );
};

export default BillCreationModal;